import type * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import moment from 'moment'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ReconcileSession } from '@webapp/modules/accounts-payable/entities/ReconcileSession'
import messages from './messages'
import { useMutateUpdateReconcileSession } from '../../models'
import { type SessionStatementControlsStatus } from '../../views/SessionStatementControls'

const MAX_BALANCE = 999999999.99

export interface IUseEditReconcileSessionPresenterParams {
  session: ReconcileSession
  /** FS recomputes overall variance from the updated balance (BHV-035/036) */
  onSessionChange: (session: ReconcileSession) => void
}

export const useEditReconcileSessionPresenter = ({
  session,
  onSessionChange,
}: IUseEditReconcileSessionPresenterParams) => {
  const intl = useProcurifyIntl()
  const { updateSession } = useMutateUpdateReconcileSession()

  const [statementDate, setStatementDate] = useState<Nullable<Date>>(
    session.statementDate
  )
  const [isDatePickerFocused, setIsDatePickerFocused] = useState(false)
  const [balanceValue, setBalanceValue] = useState(session.balance)
  const [status, setStatus] = useState<SessionStatementControlsStatus>('idle')
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  // External session refresh (React Query refetch) re-syncs the inputs.
  useEffect(() => {
    setBalanceValue(session.balance)
  }, [session.balance])

  useEffect(() => {
    setStatementDate(session.statementDate)
  }, [session.statementDate])

  const showError = useCallback(
    (error: unknown) => {
      toast.error({
        title: intl.formatMessage(messages.errorTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.genericErrorMessage),
      })
    },
    [intl]
  )

  // Persist-on-change (BHV-035); revert + error notification on failure.
  const handleStatementDateChange = useCallback(
    async (date: Date) => {
      setStatementDate(date)
      setIsDatePickerFocused(false)

      try {
        const updated = await updateSession({
          sessionId: session.id,
          creditcardId: session.creditcardId,
          statementDate: moment(date).format('YYYY-MM-DD'),
        })
        if (!isMounted.current) return
        onSessionChange(updated)
      } catch (error) {
        if (!isMounted.current) return
        setStatementDate(session.statementDate)
        showError(error)
      }
    },
    [onSessionChange, session, showError, updateSession]
  )

  const commitBalance = useCallback(async () => {
    const parsed = parseFloat(balanceValue)

    if (Number.isNaN(parsed) || parsed > MAX_BALANCE) {
      setBalanceValue(session.balance)
      return
    }

    const normalized = parsed.toFixed(2)
    setBalanceValue(normalized)

    if (normalized === session.balanceValue.toFixed(2)) return

    setStatus('pending')
    try {
      const updated = await updateSession({
        sessionId: session.id,
        creditcardId: session.creditcardId,
        balance: normalized,
      })
      if (!isMounted.current) return
      setStatus('resolved')
      onSessionChange(updated)
    } catch (error) {
      if (!isMounted.current) return
      setStatus('rejected')
      setBalanceValue(session.balance)
      showError(error)
    }
  }, [balanceValue, onSessionChange, session, showError, updateSession])

  const handleBalanceKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        commitBalance()
      } else if (event.key === 'Escape') {
        setBalanceValue(session.balance)
        setStatus('idle')
      }
    },
    [commitBalance, session.balance]
  )

  return {
    data: {
      statementDate,
      balanceValue,
      status,
      isDatePickerFocused,
    },
    methods: {
      onDatePickerFocusChange: setIsDatePickerFocused,
      onStatementDateChange: handleStatementDateChange,
      onBalanceChange: setBalanceValue,
      onBalanceBlur: commitBalance,
      onBalanceKeyDown: handleBalanceKeyDown,
    },
  }
}
